import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GitCommitHorizontal, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/controls";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/overlay";
import { Spinner, Textarea } from "@/components/ui/primitives";
import { errorMessage, get } from "@/lib/api";
import { colorForUser } from "@ide/shared";
import { logSessionEvent } from "./activity";
import { useWs } from "./context";
import { commitAndPush, readProjectTexts, type Change, type GitLink } from "./git";
import { gitKeys, saveLink } from "./git-panel";
import { logOutput } from "./output";

export interface Author {
  id: string;
  name: string;
  handle: string;
  chars: number;
}

export interface PlannedCommit {
  author: Author;
  paths: string[];
  /** Quem também mexeu nesses arquivos (Co-authored-by). */
  coAuthors: Author[];
  message: string;
}

/** E-mail público para a autoria no Git (o e-mail real da pessoa nunca sai do Kompil). */
export const noreplyEmail = (handle: string) => `${handle}@users.noreply.rrmind.tech`;

const verb = (kinds: Change["kind"][]) =>
  kinds.every((k) => k === "A") ? "Adiciona" : kinds.every((k) => k === "D") ? "Remove" : "Atualiza";

/** "Atualiza app.py e utils.py" / "Atualiza 4 arquivos em src/api". */
export function defaultMessage(changes: Change[]): string {
  const names = changes.map((c) => c.path.split("/").pop()!);
  const what =
    changes.length <= 2
      ? names.join(" e ")
      : (() => {
          const dirs = new Set(changes.map((c) => c.path.split("/").slice(0, -1).join("/")));
          const dir = dirs.size === 1 ? [...dirs][0] : "";
          return `${changes.length} arquivos${dir ? ` em ${dir}` : ""}`;
        })();
  return `${verb(changes.map((c) => c.kind))} ${what}`;
}

/**
 * Organiza as alterações em commits por pessoa: cada arquivo vai para quem mais escreveu nele
 * desde o último commit; os outros que mexeram entram como coautores. Arquivos sem autoria
 * registrada (vindos do terminal, de um pull…) ficam com quem está fazendo o commit.
 */
export function planAuthorCommits(
  changes: Change[],
  authorship: Array<{ path: string; authors: Author[] }>,
  me: Author,
): PlannedCommit[] {
  const byPath = new Map(authorship.map((f) => [f.path, f.authors]));
  const groups = new Map<string, { author: Author; changes: Change[]; co: Map<string, Author> }>();
  for (const change of changes) {
    const authors = byPath.get(change.path)?.filter((a) => a.chars > 0) ?? [];
    const main = authors[0] ?? me;
    const g = groups.get(main.id) ?? { author: main, changes: [], co: new Map<string, Author>() };
    g.changes.push(change);
    for (const a of authors.slice(1)) if (a.id !== main.id) g.co.set(a.id, a);
    groups.set(main.id, g);
  }
  return [...groups.values()]
    .sort((a, b) => b.changes.length - a.changes.length)
    .map((g) => ({
      author: g.author,
      paths: g.changes.map((c) => c.path),
      coAuthors: [...g.co.values()],
      message: defaultMessage(g.changes),
    }));
}

/** Mensagem final com os trailers de coautoria que o GitHub entende. */
export function withCoAuthors(message: string, coAuthors: Author[]): string {
  if (!coAuthors.length) return message.trim();
  return `${message.trim()}\n\n${coAuthors.map((a) => `Co-authored-by: ${a.name} <${noreplyEmail(a.handle)}>`).join("\n")}`;
}

/**
 * "Organizar em commits por autor": transforma a sessão em commits separados por pessoa, cada um
 * com o autor certo (e coautores), em vez de um commit só com tudo em nome de quem clicou.
 */
export function AuthorCommitsDialog({
  open,
  onOpenChange,
  link,
  changes,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  link: GitLink;
  changes: Change[];
}) {
  const { detail, session, me } = useWs();
  const qc = useQueryClient();
  const [messages, setMessages] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["authorship", detail.project.id, link.updatedAt],
    queryFn: () =>
      get<{ files: Array<{ path: string; authors: Author[] }> }>(
        `/projects/${detail.project.id}/authorship?since=${encodeURIComponent(link.updatedAt)}`,
      ),
    enabled: open,
  });
  const meAuthor: Author = { id: me.user.id, name: me.user.name, handle: me.user.handle, chars: 0 };
  const plan = data ? planAuthorCommits(changes, data.files, meAuthor) : [];

  const run = async () => {
    setBusy(true);
    let current = link;
    let done = 0;
    try {
      for (const c of plan) {
        const texts = await readProjectTexts(session);
        const message = withCoAuthors(messages[c.author.id] ?? c.message, c.coAuthors);
        const res = await commitAndPush(current, texts, message, new Set(c.paths), {
          name: c.author.name,
          email: c.author.id === me.user.id && me.user.email ? me.user.email : noreplyEmail(c.author.handle),
        });
        current = { ...current, baseCommit: res.commit, baseFiles: res.baseFiles };
        await saveLink(detail.project.id, current);
        done++;
        logOutput("Git", `Commit ${res.commit.slice(0, 7)} de ${c.author.name}: ${message.split("\n")[0]}`);
      }
      toast.success(
        `${done} ${done === 1 ? "commit enviado" : "commits enviados"} para ${link.branch}, um por autor.`,
      );
      logSessionEvent({
        kind: "git",
        who: me.user.name,
        text: `organizou a sessão em ${done} commits por autor em ${link.branch}`,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(
        done ? `${done} commit(s) enviados; o próximo falhou: ${errorMessage(err)}` : errorMessage(err),
      );
    } finally {
      setBusy(false);
      void qc.invalidateQueries({ queryKey: gitKeys.link(detail.project.id) });
      void qc.invalidateQueries({ queryKey: gitKeys.history(link.repo, link.branch) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent
        wide
        title="Organizar em commits por autor"
        description={`Cada pessoa vira um commit com a autoria dela em ${link.branch} — a partir de quem escreveu cada arquivo desde o último commit.`}
      >
        {isLoading ? (
          <div className="flex items-center gap-2 py-6 text-[13px] text-fg-muted">
            <Spinner className="h-4 w-4" /> Lendo quem escreveu o quê…
          </div>
        ) : (
          <ol className="max-h-[52vh] space-y-3 overflow-y-auto pr-1">
            {plan.map((c, i) => (
              <li key={c.author.id} className="rounded-lg border border-border p-3">
                <div className="mb-2 flex items-center gap-2 text-[13px]">
                  <GitCommitHorizontal className="h-4 w-4 text-fg-subtle" />
                  <span className="text-fg-subtle">{i + 1}.</span>
                  <Avatar name={c.author.name} color={colorForUser(c.author.id)} size={20} />
                  <span className="font-medium text-fg">{c.author.name}</span>
                  <span className="text-fg-muted">
                    · {c.paths.length} {c.paths.length === 1 ? "arquivo" : "arquivos"}
                  </span>
                  {c.coAuthors.length ? (
                    <span className="ml-auto flex items-center gap-1 text-[12px] text-fg-muted">
                      <Users className="h-3.5 w-3.5" /> com{" "}
                      {c.coAuthors.map((a) => a.name.split(" ")[0]).join(", ")}
                    </span>
                  ) : null}
                </div>
                <Textarea
                  rows={2}
                  value={messages[c.author.id] ?? c.message}
                  onChange={(e) => setMessages((m) => ({ ...m, [c.author.id]: e.target.value }))}
                  className="resize-none text-[12.5px]"
                  aria-label={`Mensagem do commit de ${c.author.name}`}
                />
                <p
                  className="mt-1.5 truncate font-mono text-[11.5px] text-fg-subtle"
                  title={c.paths.join("\n")}
                >
                  {c.paths.join(" · ")}
                </p>
              </li>
            ))}
          </ol>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!plan.length || plan.some((c) => !(messages[c.author.id] ?? c.message).trim())}
            onClick={() => void run()}
          >
            Fazer {plan.length} {plan.length === 1 ? "commit" : "commits"} e push
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
